import { Chip } from '@mui/material';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { crudStatusEnum, getCrudStatusLabel, localStorageKeys, permissionLevelEnum } from '../../../../constants';
import { getCrudStatusColor } from '../../../../constants/colors';
import { useUpdateWasteCodeMutation } from '../../../../hooks/mutations/useWasteCodeMutation';
import { useWasteCodesQuery } from '../../../../hooks/queries/useWasteCodesQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { WasteCode } from '../../../../services/apiSLTP/materials/wasteCodes';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { createDateTimeColumn } from '../../../../utils/tables/commonColumns';
import StateCell from '../../../cells/StateCell';
import UpdateWasteCodeModal from '../../../modals/admin/configuration/UpdateWasteCodeModal';
import DeleteModal from '../../../modals/DeleteModal';
import IconButton from '../../../ui/IconButton';
import IconLine from '../../../ui/IconLine';
import GenericAgGridTable from '../../GenericAgGridTable';

interface WasteCodesSettingsRowProps {
    withUsageCount: boolean;
}

const WasteCodesSettingsRow: React.FC<WasteCodesSettingsRowProps> = ({ withUsageCount }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedWasteCode, setSelectedWasteCode] = useState<WasteCode | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);

    const {
        data: wasteCodes,
        isLoading: isWasteCodesLoading,
        refetch: refetchWasteCodes,
    } = useWasteCodesQuery({
        queries: { withUsage: withUsageCount },
    });

    const {
        mutate: mutateWasteCode,
        isSuccess: isSuccessWasteCode,
        isError: isErrorWasteCode,
        isLoading: isLoadingWasteCode,
        error: errorWasteCode,
    } = useUpdateWasteCodeMutation({
        onSuccess: () => refetchWasteCodes(),
        onError: (error) => console.error('Error updating waste code:', error.message),
    });

    const columns: ColDef<WasteCode>[] = useMemo<ColDef<WasteCode>[]>(
        () => [
            {
                headerName: capitalizeFirstLetter(t('name')),
                field: 'designation',
                colId: 'designation',
                cellRenderer: (params: ICellRendererParams<WasteCode>) => {
                    if (!params.data) return params.value;
                    return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span>{params.value}</span>
                            {params.data.designation.endsWith('*') && <Chip label="⚠️" size="small" color="error" sx={{ height: 20 }} />}
                        </div>
                    );
                },
            },
            {
                headerName: capitalizeFirstLetter(t('description')),
                field: 'description',
                initialFlex: 1,
                minWidth: 400,
                colId: 'description',
                cellStyle: { whiteSpace: 'pre-wrap' },
            },
            {
                headerName: capitalizeFirstLetter(t('actions')),
                cellRenderer: (params: ICellRendererParams<WasteCode>) => {
                    if (!params.data) return null;
                    return (
                        <IconLine>
                            <IconButton
                                iconName="edit"
                                title={capitalizeFirstLetter(t('modifyWasteCode'))}
                                onClick={() => {
                                    if (!params.data) return;
                                    setSelectedWasteCode(params.data);
                                    setIsEditMode(true);
                                    setShowEditModal(true);
                                }}
                                disabled={params.data.crudStatus === crudStatusEnum.DELETED}
                            />
                            {user && user.profileId.permsLevel <= permissionLevelEnum.ADMIN && (
                                <IconButton
                                    iconName="delete"
                                    title={capitalizeFirstLetter(t('deleteWasteCode'))}
                                    onClick={() => {
                                        if (!params.data) return;
                                        setSelectedWasteCode(params.data);
                                        setShowDeleteModal(true);
                                    }}
                                    disabled={params.data.crudStatus === crudStatusEnum.DELETED}
                                />
                            )}
                        </IconLine>
                    );
                },
                colId: 'actions',
                filter: false,
                enableRowGroup: false,
                enableValue: false,
                sortable: false,
            },
            {
                headerName: capitalizeFirstLetter(t('status')),
                valueGetter: (params) => capitalizeFirstLetter(t(getCrudStatusLabel(params.data?.crudStatus))),
                cellRenderer: (params: ICellRendererParams<WasteCode>) => {
                    if (!params.data) return params.value;
                    return (
                        <StateCell stateColor={getCrudStatusColor(params.data?.crudStatus)} text={t(getCrudStatusLabel(params.data?.crudStatus))} />
                    );
                },
                colId: 'status',
            },
            createDateTimeColumn<WasteCode>('createdAt', 'createdAt'),
            createDateTimeColumn<WasteCode>('updatedAt', 'updatedAt'),
            ...((user?.profileId.permsLevel === permissionLevelEnum.ADMIN
                ? [
                      {
                          headerName: capitalizeFirstLetter(t('_id')),
                          field: '_id',
                          colId: 'id',
                          cellClass: 'usage-col-main',
                          headerClass: 'usage-col-main',
                          initialWidth: 250,
                      },
                      {
                          headerName: capitalizeFirstLetter(t('xTotal')),
                          field: 'usageCount.total',
                          colId: 'xtotal',
                          cellClass: 'usage-col',
                          headerClass: 'usage-col',
                          filter: 'agNumberColumnFilter',
                      },
                      {
                          headerName: capitalizeFirstLetter(t('xRidesPlanned')),
                          field: 'usageCount.dailyTrucksPlanned',
                          colId: 'xridesplanned',
                          cellClass: 'usage-col',
                          headerClass: 'usage-col',
                          filter: 'agNumberColumnFilter',
                      },
                      {
                          headerName: capitalizeFirstLetter(t('xRidesCompleted')),
                          field: 'usageCount.dailyTrucksRides',
                          colId: 'xridescompleted',
                          cellClass: 'usage-col',
                          headerClass: 'usage-col',
                          filter: 'agNumberColumnFilter',
                      },
                  ]
                : []) as ColDef<WasteCode>[]),
        ],
        [t, user]
    );

    const sortedWasteCodes = useMemo(() => {
        return wasteCodes?.sort((a, b) => a.designation.localeCompare(b.designation)) || [];
    }, [wasteCodes]);

    return (
        <>
            <GenericAgGridTable
                columnDefs={columns}
                rowData={sortedWasteCodes}
                loading={isWasteCodesLoading}
                headerRightComponent={
                    <IconButton
                        iconName="addCircle"
                        onClick={() => {
                            setSelectedWasteCode(null);
                            setIsEditMode(false);
                            setShowEditModal(true);
                        }}
                        title={capitalizeFirstLetter(t('addWasteCode'))}
                    />
                }
                exportCsvFileName="admin-waste-codes"
                storageKey={localStorageKeys.TABLE.ADMIN.MATERIAL.WASTE}
                agGridProps={{ getRowId: ({ data }) => data._id }}
            />

            {showEditModal && (
                <UpdateWasteCodeModal
                    showEditModal={showEditModal}
                    setShowEditModal={setShowEditModal}
                    wasteCode={selectedWasteCode}
                    isEditMode={isEditMode}
                    refetch={refetchWasteCodes}
                />
            )}
            {showDeleteModal && selectedWasteCode && (
                <DeleteModal
                    open={showDeleteModal}
                    handleClose={() => setShowDeleteModal(false)}
                    deleteAction={() => {
                        mutateWasteCode({ wasteCodeId: selectedWasteCode._id, data: { crudStatus: crudStatusEnum.DELETED } });
                    }}
                    title={t('wasteCode')}
                    itemName={selectedWasteCode.designation}
                />
            )}
        </>
    );
};

export default WasteCodesSettingsRow;
